import React, { useCallback } from 'react';
import { useModal } from '../Modal/useModal';
import { ModalConfig } from '../Modal/ModalContext';
import Button, { ButtonGroup } from '../Button/Button';
import { useTranslation } from 'utils/translation';
import Section from '../Section/Section';
import Spacer from '../Spacer/Spacer';
import Typography from '../Typography/Typography';

type ConfirmationOptions = {
  title?: string;
  question?: React.ReactNode;
  cancelText?: string;
  confirmText?: string;
  onConfirm?(): void;
  onCancel?(): void;
  children?: React.ReactNode;
  /** Passed to the modal wrapper (default `default`; replaces previous invalid `xs`). */
  modalSize?: ModalConfig['size'];
};

const useConfirmation = () => {
  const modal = useModal();
  const { t } = useTranslation();

  return useCallback(
    (options: ConfirmationOptions = {}) => {
      return new Promise<boolean>((resolve) => {
        const opts = {
          ...options,
          title: options.title ?? t('confirmation.title'),
          question: options.question ?? t('confirmation.question'),
          cancelText: options.cancelText ?? t('confirmation.answer_no'),
          confirmText: options.confirmText ?? t('confirmation.answer_yes'),
        };

        const Component = () => (
          <Section padding="small">
            {opts.question && (
              <>
                <Typography align="center">{opts.question}</Typography>
                <Spacer size={opts.children !== null ? 2 : 4} />
              </>
            )}
            {opts.children}
          </Section>
        );

        modal.open(Component, {
          size: opts.modalSize ?? 'default',
          disableClose: true,
          disableBackdropClose: true,
          disableEscapeKeyDown: true,
          title: opts.title,
          footer: () => (
            <ButtonGroup align="right">
              <Button
                size="small"
                variant="cancel"
                onClick={() => {
                  options.onCancel && options.onCancel();
                  modal.close(Component);
                  resolve(false);
                }}
              >
                {opts.cancelText}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  opts.onConfirm && opts.onConfirm();
                  modal.close(Component);
                  resolve(true);
                }}
              >
                {opts.confirmText}
              </Button>
            </ButtonGroup>
          ),
        });
      });
    },
    [modal.open],
  );
};

export default useConfirmation;
